/**
 * PHASE 11: Real Jupiter Swap Integration
 * 
 * This service handles actual on-chain SOL → $SLAP swaps via Jupiter Aggregator.
 * Platform fee is deducted BEFORE the swap by reducing the swap amount.
 */

import { 
  Connection, 
  VersionedTransaction, 
  Keypair 
} from '@solana/web3.js';
import bs58 from 'bs58';

// Jupiter API endpoints
const JUPITER_QUOTE_API = 'https://quote-api.jup.ag/v6/quote';
const JUPITER_SWAP_API = 'https://quote-api.jup.ag/v6/swap';

// Token mint addresses
const SOL_MINT = 'So11111111111111111111111111111111111111112';
const SLAP_TOKEN_MINT = 'A7rKF3srg28UArWp1ibVVUbQmnjUnitTtq7ZFR4tpump';

// ============================================================
// TREASURY WALLET - ALL FEES GO HERE (NO BURN, HOUSE PROFIT)
// ============================================================
const PLATFORM_FEE_PERCENT = 1.0; // 1% fee on swaps
const PLATFORM_FEE_WALLET = 'GntfPdLmhjJ2unKJ9fp7S6rDz7AA1Hkn52pKpd9TceCa'; // Treasury Wallet (House)

/**
 * Convert SOL to lamports (1 SOL = 1,000,000,000 lamports)
 */
const solToLamports = (sol) => Math.floor(sol * 1_000_000_000);

/**
 * Convert lamports to SOL
 */
const lamportsToSol = (lamports) => lamports / 1_000_000_000;

/**
 * Calculate platform fee and net swap amount
 */
export const calculateFeeAndAmount = (solAmount) => {
  const feeAmount = solAmount * (PLATFORM_FEE_PERCENT / 100);
  const netAmount = solAmount - feeAmount;
  return {
    grossAmount: solAmount,
    feeAmount: feeAmount,
    netAmount: netAmount,
    feePercent: PLATFORM_FEE_PERCENT
  };
};

/**
 * Get a quote from Jupiter for SOL → $SLAP swap
 */
export const getJupiterQuote = async (solAmount, slippageBps = 50) => {
  const lamports = solToLamports(solAmount);
  
  const params = new URLSearchParams({
    inputMint: SOL_MINT,
    outputMint: SLAP_TOKEN_MINT,
    amount: lamports.toString(),
    slippageBps: slippageBps.toString(),
    onlyDirectRoutes: 'false',
    asLegacyTransaction: 'false'
  });
  
  const response = await fetch(`${JUPITER_QUOTE_API}?${params}`);
  
  // FIX: Read body as text first, then parse - prevents "Body is disturbed" error
  const text = await response.text();
  const data = text ? JSON.parse(text) : {};
  
  if (!response.ok) {
    throw new Error(data.error || `Jupiter quote failed: ${response.status}`);
  }
  
  return data;
};

/**
 * Get a quote from Jupiter for $SLAP → SOL swap (SELL)
 */
export const getJupiterSellQuote = async (slapAmount, slippageBps = 100) => {
  // $SLAP has 6 decimals
  const slapSmallestUnit = Math.floor(slapAmount * 1_000_000);
  
  const params = new URLSearchParams({
    inputMint: SLAP_TOKEN_MINT,  // Selling $SLAP
    outputMint: SOL_MINT,        // For SOL
    amount: slapSmallestUnit.toString(),
    slippageBps: slippageBps.toString(),
    onlyDirectRoutes: 'false',
    asLegacyTransaction: 'false'
  });
  
  const response = await fetch(`${JUPITER_QUOTE_API}?${params}`);
  
  // FIX: Read body as text first, then parse - prevents "Body is disturbed" error
  const text = await response.text();
  const data = text ? JSON.parse(text) : {};
  
  if (!response.ok) {
    throw new Error(data.error || `Jupiter sell quote failed: ${response.status}`);
  }
  
  return data;
};

/**
 * Build swap transaction from Jupiter
 */
export const buildSwapTransaction = async (quoteResponse, userPublicKey) => {
  const response = await fetch(JUPITER_SWAP_API, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      quoteResponse,
      userPublicKey: userPublicKey,
      wrapAndUnwrapSol: true,
      dynamicComputeUnitLimit: true,
      dynamicSlippage: true,
      prioritizationFeeLamports: {
        priorityLevelWithMaxLamports: {
          priorityLevel: 'high',
          maxLamports: 1000000 // 0.001 SOL max priority fee
        }
      }
    })
  });
  
  // FIX: Read body as text first, then parse - prevents "Body is disturbed" error
  const text = await response.text();
  const data = text ? JSON.parse(text) : {};
  
  if (!response.ok) {
    throw new Error(data.error || `Jupiter swap build failed: ${response.status}`);
  }
  
  return data;
};

/**
 * Sign and send transaction to Solana
 */
export const signAndSendTransaction = async (swapTransaction, secretKeyBase58, rpcUrl) => {
  // Decode the transaction
  const transactionBuf = Buffer.from(swapTransaction, 'base64');
  const transaction = VersionedTransaction.deserialize(transactionBuf);
  
  // Create keypair from secret key
  const secretKeyBytes = bs58.decode(secretKeyBase58);
  const keypair = Keypair.fromSecretKey(secretKeyBytes);
  
  // Sign the transaction
  transaction.sign([keypair]);
  
  // Connect to Solana and send
  const connection = new Connection(rpcUrl, 'confirmed');
  
  // Send with retry logic
  const signature = await connection.sendTransaction(transaction, {
    skipPreflight: true,
    maxRetries: 3
  });
  
  return signature;
};

/**
 * Confirm transaction on-chain
 */
export const confirmTransaction = async (signature, rpcUrl, timeout = 60000) => {
  const connection = new Connection(rpcUrl, 'confirmed');
  
  const startTime = Date.now();
  
  while (Date.now() - startTime < timeout) {
    const status = await connection.getSignatureStatus(signature);
    
    if (status?.value?.confirmationStatus === 'confirmed' || 
        status?.value?.confirmationStatus === 'finalized') {
      return {
        confirmed: true,
        signature,
        status: status.value.confirmationStatus,
        error: null
      };
    }
    
    if (status?.value?.err) {
      return {
        confirmed: false,
        signature,
        status: 'failed',
        error: status.value.err
      };
    }
    
    // Wait before next check
    await new Promise(r => setTimeout(r, 2000));
  }
  
  return {
    confirmed: false,
    signature,
    status: 'timeout',
    error: 'Transaction confirmation timed out'
  };
};

/**
 * Execute a complete SOL → $SLAP swap with platform fee
 * 
 * Flow:
 * 1. Calculate platform fee (1%)
 * 2. Get Jupiter quote for net amount (after fee)
 * 3. Build swap transaction
 * 4. Sign and send transaction
 * 5. Confirm on-chain
 * 
 * NOTE: The platform fee is collected by reducing the swap amount.
 * The remaining SOL in the wallet IS the fee collected.
 */
export const executeRealSwap = async ({
  solAmount,
  userPublicKey,
  secretKeyBase58,
  rpcUrl,
  slippageBps = 100, // 1% slippage default
  onProgress
}) => {
  try {
    // Step 1: Calculate fee
    onProgress?.({ step: 'calculating', message: 'Calculating fees...' });
    const feeCalc = calculateFeeAndAmount(solAmount);
    
    // Step 2: Get Jupiter quote for the NET amount (after fee deduction)
    onProgress?.({ step: 'quoting', message: 'Getting best swap route...' });
    const quoteResponse = await getJupiterQuote(feeCalc.netAmount, slippageBps);
    
    if (!quoteResponse || !quoteResponse.outAmount) {
      throw new Error('No swap route available for this token pair');
    }
    
    // Step 3: Build transaction
    onProgress?.({ step: 'building', message: 'Building transaction...' });
    const swapData = await buildSwapTransaction(quoteResponse, userPublicKey);
    
    if (!swapData.swapTransaction) {
      throw new Error('Failed to build swap transaction');
    }
    
    // Step 4: Sign and send
    onProgress?.({ step: 'signing', message: 'Signing & broadcasting...' });
    const signature = await signAndSendTransaction(
      swapData.swapTransaction,
      secretKeyBase58,
      rpcUrl
    );
    
    // Step 5: Confirm
    onProgress?.({ step: 'confirming', message: 'Confirming on-chain...' });
    const confirmation = await confirmTransaction(signature, rpcUrl);
    
    if (!confirmation.confirmed) {
      throw new Error(confirmation.error || 'Transaction failed to confirm');
    }
    
    return {
      success: true,
      signature,
      inputAmount: solAmount,
      feeAmount: feeCalc.feeAmount,
      netSwapAmount: feeCalc.netAmount,
      outputAmount: parseInt(quoteResponse.outAmount),
      explorerUrl: `https://solscan.io/tx/${signature}`
    };
    
  } catch (error) {
    console.error('Jupiter swap error:', error);
    throw error;
  }
};

/**
 * Get estimated output for a swap (for UI preview)
 */
export const getSwapEstimate = async (solAmount) => {
  const feeCalc = calculateFeeAndAmount(solAmount);
  
  try {
    const quote = await getJupiterQuote(feeCalc.netAmount);
    
    return {
      inputSol: solAmount,
      feeSol: feeCalc.feeAmount,
      netSol: feeCalc.netAmount,
      feePercent: feeCalc.feePercent,
      outputTokens: parseInt(quote.outAmount),
      priceImpact: quote.priceImpactPct || '0',
      route: quote.routePlan?.map(r => r.swapInfo?.label).join(' → ') || 'Direct'
    };
  } catch (error) {
    console.error('Quote error:', error);
    return null;
  }
};

/**
 * Get estimated output for SELL swap ($SLAP → SOL)
 */
export const getSellEstimate = async (slapAmount) => {
  try {
    const quote = await getJupiterSellQuote(slapAmount);
    
    if (!quote || !quote.outAmount) {
      return null;
    }
    
    // Calculate output SOL (outAmount is in lamports)
    const outputSol = lamportsToSol(parseInt(quote.outAmount));
    
    // Calculate platform fee (1% of output)
    const feeAmount = outputSol * (PLATFORM_FEE_PERCENT / 100);
    const netOutput = outputSol - feeAmount;
    
    return {
      inputSlap: slapAmount,
      outputSol: outputSol,
      feeSol: feeAmount,
      netSol: netOutput,
      feePercent: PLATFORM_FEE_PERCENT,
      priceImpact: quote.priceImpactPct || '0',
      route: quote.routePlan?.map(r => r.swapInfo?.label).join(' → ') || 'Direct'
    };
  } catch (error) {
    console.error('Sell quote error:', error);
    return null;
  }
};

/**
 * Execute a complete $SLAP → SOL swap (SELL) with platform fee
 */
export const executeSellSwap = async ({
  slapAmount,
  userPublicKey,
  secretKeyBase58,
  rpcUrl,
  slippageBps = 150, // 1.5% slippage default for sell
  onProgress
}) => {
  try {
    // Step 1: Get Jupiter quote for SLAP → SOL
    onProgress?.({ step: 'quoting', message: 'Getting best sell route...' });
    const quoteResponse = await getJupiterSellQuote(slapAmount, slippageBps);
    
    if (!quoteResponse || !quoteResponse.outAmount) {
      throw new Error('No sell route available for $SLAP');
    }
    
    // Step 2: Build transaction
    onProgress?.({ step: 'building', message: 'Building sell transaction...' });
    const swapData = await buildSwapTransaction(quoteResponse, userPublicKey);
    
    if (!swapData.swapTransaction) {
      throw new Error('Failed to build sell transaction');
    }
    
    // Step 3: Sign and send
    onProgress?.({ step: 'signing', message: 'Signing & broadcasting...' });
    const signature = await signAndSendTransaction(
      swapData.swapTransaction,
      secretKeyBase58,
      rpcUrl
    );
    
    // Step 4: Confirm
    onProgress?.({ step: 'confirming', message: 'Confirming on-chain...' });
    const confirmation = await confirmTransaction(signature, rpcUrl);
    
    if (!confirmation.confirmed) {
      throw new Error(confirmation.error || 'Sell transaction failed to confirm');
    }
    
    // Calculate outputs
    const outputSol = lamportsToSol(parseInt(quoteResponse.outAmount));
    
    return {
      success: true,
      signature,
      inputAmount: slapAmount,
      outputAmount: outputSol,
      explorerUrl: `https://solscan.io/tx/${signature}`
    };
    
  } catch (error) {
    console.error('Jupiter sell swap error:', error);
    throw error;
  }
};

export default {
  executeRealSwap,
  executeSellSwap,
  getSwapEstimate,
  getSellEstimate,
  getJupiterQuote,
  getJupiterSellQuote,
  calculateFeeAndAmount
};
